import ImageHelper, {
  Container as ImageHelperContainer,
} from "@components/ImageHelper"
import animation, { cubicBezier } from "@constants/animation"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { body14Styles } from "@constants/typography"
import { Link } from "gatsby"
import React from "react"
import styled from "styled-components"

const DisabledLink = styled.div<{ $compact: boolean }>`
  cursor: not-allowed;
  flex-shrink: 0;
  width: ${({ $compact }) =>
    $compact ? "calc((100% - 36px) / 3.75)" : "calc((100% - 24px) / 3)"};
  &:not(:last-child) {
    margin-right: 12px;
  }
`

const GatsbyLink = styled(Link)<{ $active: boolean; $compact: boolean }>`
  ${({ $active, to }) =>
    !to.length || !!$active ? "pointer-events: none" : "cursor: pointer"};
  flex-shrink: 0;
  width: ${({ $compact }) =>
    $compact ? "calc((100% - 36px) / 3.75)" : "calc((100% - 24px) / 3)"};
  &:not(:last-child) {
    margin-right: 12px;
  }
`

const Container = styled.div`
  flex-direction: column;
  position: relative;
  display: flex;
  width: 100%;
`

const Content = styled.div`
  width: 100%;
`

const ImageWrapper = styled.div<{ $active: boolean }>`
  position: relative;
  flex-direction: column;
  flex-grow: 1;
  justify-content: flex-end;
  display: flex;
  padding: 4px;
  transition: background-color ${animation.medium} ${cubicBezier};
  background-color: ${colors.backgroundGray};
  border: ${({ $active }) =>
    $active ? `1px solid ${colors.black}` : "1px solid transparent"};

  @media ${breakpoints.laptop} {
    align-self: stretch;
    justify-self: stretch;
    padding: 4px;

    &:hover {
      background-color: ${colors.backgroundGrayHover};
    }
  }

  ${ImageHelperContainer} {
    flex-grow: 0;
    flex-shrink: 0;
    width: 100%;
    height: auto;

    img {
      object-fit: contain;
    }
  }
`

const Title = styled.div`
  ${body14Styles}
  align-items: center;
  justify-content: center;
  display: flex;
  margin-top: 4px;
  color: ${colors.black};
`

const Price = styled.div`
  ${body14Styles}
  align-items: center;
  justify-content: center;
  display: flex;
  opacity: 0.9;
  color: ${colors.mediumDarkGray};
`

export interface SeriesBlockProps {
  active: boolean
  compact: boolean
  disabled?: boolean
  imageUrl: string
  name: string
  price: string
  slug: string
}

const SeriesBlock: React.FC<SeriesBlockProps> = ({
  active,
  compact,
  disabled = false,
  imageUrl,
  name,
  price,
  slug,
}: SeriesBlockProps) => {
  return disabled ? (
    <DisabledLink $compact={compact} title="Not Available">
      <Container>
        <ImageWrapper $active={active}>
          <ImageHelper src={imageUrl} />
        </ImageWrapper>
        <Content>
          <Title>{name}</Title>
          <Price>{`$${price}`}</Price>
        </Content>
      </Container>
    </DisabledLink>
  ) : (
    <GatsbyLink $active={active} $compact={compact} to={slug}>
      <Container>
        <ImageWrapper $active={active}>
          <ImageHelper src={imageUrl} />
        </ImageWrapper>
        <Content>
          <Title>{name}</Title>
          <Price>{`$${price}`}</Price>
        </Content>
      </Container>
    </GatsbyLink>
  )
}

export default SeriesBlock
